import {HttpClient} from '@angular/common/http';
import {
  BehaviorSubject, catchError, filter, forkJoin, lastValueFrom, map, Observable, of,
  retry, Subject, switchMap, take, takeUntil, tap, timer
} from 'rxjs';
import {Inject, Injectable} from '@angular/core';
import {EnvironmentService} from './environment.service';
import {JwtHelperService} from '@auth0/angular-jwt';
import {UserLoginModel} from '../models/user-login.model';
import {Router} from '@angular/router';
import {GrantsModel} from '../models/grants.model';
import {ClientLoginService} from './client-login.service';
import {LOCATION_TOKEN} from '../constants/location.token';
import {ConsoleLogger} from '../shared/console-logger';
import {Application} from 'src/portaljs/constants/application';
import {Util} from '../shared/util';
import * as _ from 'lodash-es';
import {ThemeService} from './theme.service';
import {ThemeModel} from '../models/theme.model';
import LoginPrompt from '../enums/login-prompt.enum';
import {AuthenticationEvent as AuthenticationEvent} from '../models/authentication-event.model';
import {MessageCode, UserMessage} from '../shared/error/user-message';
import {ErrorDisplayData} from '../models/error-display-data.model';
import Scope from '../enums/scopes.enum';
import AuthType from '../enums/auth.type.enum';
import * as uuid from 'uuid';
import {v4 as uuidv4} from 'uuid';
import PopupState from '../enums/popup.state.enum';
import ImsRole from '../enums/ims-role.enum';
import {ToastMessageService} from './toast-message.service';
import {DialogModeType} from '../shared/error/dialog-message-type.enum';
import {LoginAction} from '../models/login-action';
import {MLMarketingURLUtils} from '../shared/masterlibrary_marketing_urls';
import { LoginTokenModel } from '../models/login.token.model';
import { UnifiedSettingsService } from './unified-settings.service';
import { DebugMessage } from '../models/portal-debug-message';
import { version } from 'src/version';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private refreshUnsubscribe = new Subject();

  private _userLogin: BehaviorSubject<UserLoginModel> = new BehaviorSubject<UserLoginModel>(undefined);
  private userLogin$: Observable<UserLoginModel> = this._userLogin.asObservable();
  private _authenticationEvent: BehaviorSubject<AuthenticationEvent> = new BehaviorSubject<AuthenticationEvent>(undefined);
  private authenticationEvent$: Observable<AuthenticationEvent> = this._authenticationEvent.asObservable();
  private _tokenRedeemed: BehaviorSubject<UserLoginModel> = new BehaviorSubject<UserLoginModel>(undefined);
  private tokenRedeemed$: Observable<UserLoginModel> = this._tokenRedeemed.asObservable();
  private _removeUser: BehaviorSubject<ErrorDisplayData> = new BehaviorSubject<ErrorDisplayData>(undefined);
  private removeUser$: Observable<ErrorDisplayData> = this._removeUser.asObservable();

  private _grants: BehaviorSubject<GrantsModel> = new BehaviorSubject<GrantsModel>(undefined);
  private grants$: Observable<GrantsModel> = this._grants.asObservable();
  private AASP_ACCESS_TOKEN_DURATION = 300000; // 5 mins
  private jwtHelper = new JwtHelperService();
  private districtAppId: string;
  private siteGuid: string;
  private accessTokenCheckInterval: Subject<void> = new Subject<void>();
  private refreshInProgress: boolean = false;
  public temporary_deep_link: any;
  public temporary_continuation_state: string;

  public static readonly CONTINUATION_STATE_NAV_TOKEN = 'pcstateNavInfo';
  /**
   * Strips a leading CONTINUATION_STATE_NAV_TOKEN prefix if present, so repeated
   * calls to prepend the token don't stack multiple copies onto an already-tokenized value.
   */
  public static stripNavToken(value: string): string {
    return value?.replace(new RegExp(`^(?:${AuthService.CONTINUATION_STATE_NAV_TOKEN})+`), '') ?? value;
  }

  public terminal_id: string = '';
  public siteGuidParam: any;
  public noIPAuthNextLogin: boolean = false;

  public _appsFramed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public appsFramed$: Observable<boolean> = this._appsFramed.asObservable();
  public _appsSelected: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public appsSelected$: Observable<boolean> = this._appsSelected.asObservable();

  constructor(private http: HttpClient,
              private envService: EnvironmentService,
              private router: Router,
              private clientLoginService: ClientLoginService,
              @Inject(LOCATION_TOKEN) private location: Location,
              private themeService: ThemeService,
              private toastService: ToastMessageService,
              private unService: UnifiedSettingsService) {
      this.initialize();
  }

  private initialize() {
    this.terminal_id = localStorage.getItem('fss_terminal_id');
    if (_.isEmpty(this.terminal_id)) {
      this.terminal_id = uuidv4();
      localStorage.setItem('fss_terminal_id', this.terminal_id);
    }

  }

  public setGrants(authEvent: GrantsModel) {
    this._grants.next(authEvent);
  }
  public getGrants$(): Observable<GrantsModel> {
    return this.grants$;
  }
  public getGrants(): GrantsModel {
    return this._grants.value;
  }

  public getTokenRedeemed$(): Observable<UserLoginModel> {
    return this.tokenRedeemed$;
  }

  public getAccessToken(): string {
    return localStorage.getItem('accessToken');
  }
  public setAccessToken( token: string ) {
    localStorage.setItem( 'accessToken', token );
  }

  public setAppsFramed(framed: boolean) {
    this._appsFramed.next(framed);
  }
  public setAppsSelected(selected: boolean) {
    this._appsSelected.next(selected);
  }

  public initiateAuthentication(authEvent: AuthenticationEvent) {
    this.setAuthenticationEvent(authEvent);
  }
  public setAuthenticationEvent(authEvent: AuthenticationEvent) {
    this._authenticationEvent.next(authEvent);
  }
  public getAuthenticationEvent$(): Observable<AuthenticationEvent> {
    return this.authenticationEvent$;
  }
  public getAuthenticationEvent(): AuthenticationEvent {
    return this._authenticationEvent.value;
  }
  public setUserLogin(ulm: UserLoginModel) {
    if (!ulm) {
      // NOTE that setting ulm to undefined will cause an immediate removal of all embedded iframes!!!
      localStorage.removeItem('idToken');
      localStorage.removeItem('accessToken');
      this._userLogin.next(undefined); // ULM must be set before updating grants!
      this.setGrants(undefined);
    } else {
      this._userLogin.next(ulm);
    }
  }

  public getUserLogin$(): Observable<UserLoginModel> {
    return this.userLogin$;
  }

  public getUserLogin(): UserLoginModel {
    return this._userLogin.getValue();
  }

  public isLoggedIn(): boolean {
    return !!this._userLogin.getValue();
  }

  public getSiteGuid(): string {
    return this.siteGuid || localStorage.getItem('siteGuid');
  }
  public setSiteGuid(guid: string) {
    this.siteGuid = guid;
    if (guid) {
      localStorage.setItem('siteGuid', guid);
    } else {
      localStorage.removeItem('siteGuid');
    }
  }
  public getSiteGuidWithoutContext(val:string): string {
    if(val && val.trim() !== ''){
      const siteMatch = val.match(/site:([A-Z0-9-]+)/i);
      if (siteMatch && siteMatch[1]) {
        return siteMatch[1];
      } else {
        return val;
      }
    } else {
      return undefined;
    }
  }

  public getDistrictAppId(): string {
    return this.districtAppId || localStorage.getItem('district_appId');
  }
  public setDistrictAppId(id: string) {
    this.districtAppId = id;
    if (id) {
      localStorage.setItem('district_appId', id);
    } else {
      localStorage.removeItem('district_appId');
    }
  }

  public isDistrictUser(): boolean {
    return !this.isDestinyAdmin() && !!this.getUserLogin()?.isDistrictUser();
  }
  public isDistrictUserAtSite(): boolean {
    return !!(this.isDistrictUser()  && !this.isDestinyAdmin() && this.getUserLogin()?.getSiteGuid());
  }
  public isDistrictUserNotAtSite(): boolean {
    return !!(this.isDistrictUser() && !this.isDestinyAdmin() && !this.getUserLogin()?.getSiteGuid());
  }
  public isDestinyAdminAtSite(): boolean {
    return !!(this.isDestinyAdmin() && this.getUserLogin()?.getSiteGuid());
  }
  public isDestinyAdminNotAtSite(): boolean {
    return !!(this.isDestinyAdmin() && !this.getUserLogin()?.getSiteGuid());
  }
  public isSiteAdmin() {
    let isSiteAdmin = false;
    const grants: GrantsModel = this.getGrants();
    if (grants) {
      const scopes: Scope[] = this.getGrants().scopes;
      isSiteAdmin = scopes.indexOf(Scope.DESTINY_BACKOFFICE_ADMIN) >= 0;
    }
    return isSiteAdmin;
  }
  public isNotSiteAdmin() {
    return !this.isSiteAdmin();
  }
  public isDestinyAdmin() {
    return !!this.getUserLogin()?.isDestinyAdmin();
  }
  public isUnauthenticatedUser() {
    return this.getUserLogin() === undefined;
  }
  public isGuestUser() {
    return !!this.getUserLogin()?.isGuestUser();
  }
  public isSharedUser() {
    return !!this.getUserLogin()?.isSharedUser();
  }
  public isAuthenticatedSiteUser() {
    return (!this.isDistrictUser() && !this.isGuestUser() && !this.isSharedUser());
  }

  public isDestinyForcePasswordReset() {
    if (this.getUserLogin()?.isDestinyForcePasswordReset()) {
      return (!this.isGuestUser() && !this.isSharedUser());
    }
    return false;
  }

  public async setUserLoginModelFromLocalStorageIfNotExists() {
    if (!this.getUserLogin()) { // ulm will never be set if this function is called from the app.module
      const proxyCheckComplete = localStorage.getItem('proxy-check-complete');
      ConsoleLogger.debug('xxxx authSvc: ' + 'setUserLoginModelFromLocalStorageIfNotExists() - proxyCheckComplete: ' + proxyCheckComplete);
      const currentUrl = this.location.href;
      if(proxyCheckComplete && proxyCheckComplete === 'true' && !currentUrl.includes('code=')) {
        try {
          const params = {
            state: true
          };
          ConsoleLogger.debug('^^^^ AuthService - setUserLoginModelFromLocalStorageIfNotExists - refreshing token...');
          const tokens: any = await lastValueFrom(this.refreshToken(5000, 1, params));

          if (tokens && tokens.idToken) {
            const ulm: UserLoginModel = new UserLoginModel(this.getClaimsFromToken(tokens.idToken), tokens);
            this.setDistrictAppId(ulm.getAppId());
            localStorage.setItem('idToken', tokens.idToken);

            await lastValueFrom(this.unService.fetchPortalUserSetting(ulm), { defaultValue: undefined });
            this.setUserLogin(ulm);
            this.setSiteGuid(ulm.getSiteGuid());
          } else {
            this.setUserLogin(undefined); // clears the user login object and localstorage
          }
        } catch (error) {
          ConsoleLogger.error('Error refreshing token: ' + error);
          this.setUserLogin(undefined); // clears the user login object and localstorage
        }
      }
    }
    return of(undefined);
  }

  public login(loginAction: LoginAction, addl?: string) {
    this.newLoginInitiated();
    loginAction.districtAppId = loginAction.districtAppId || this.getDistrictAppId();
    if (!loginAction.ignorePreferredSite) {
      loginAction.siteGuid = loginAction.siteGuid || this.getSiteGuid();
    }
    let url = '';
    if(this.envService.isBrowserPopupBlocked()) {
      loginAction.fullscreen = true;
    } else {
      loginAction.continuationState = undefined;
    }
    loginAction.noIPAuth = this.noIPAuthNextLogin;
    this.noIPAuthNextLogin = false;

    url = this.getAuthenticationUrl(loginAction);
    const popupState = loginAction.loginPrompt === LoginPrompt.LOGIN ? PopupState.LOGIN : PopupState.SITE_SWITCH;
    if(loginAction.fullscreen) {
      localStorage.setItem('proxy-check-complete', 'false');
      ConsoleLogger.debug('xxxx AuthService.login() - lswitch url: ' + url);
      this.logFailInfo(undefined, {fn: 'login', addl: addl, lp: loginAction.loginPrompt, fa: loginAction.fullscreen}).pipe(take(1)).subscribe();
      this.envService.reloadEntireApp(url);
    } else {
      this.clientLoginService.open(url, popupState);
    }
  }

  private newLoginInitiated() {
    this._appsFramed.next(false);
    this._appsSelected.next(false);
  }

  public switchSiteLogin(siteGuid?: string) {
    this.newLoginInitiated();
    const url = this.envService.getPortalBackendUrl() + '/authenticate/switch?site=' + (siteGuid === undefined ? '': siteGuid);
    return this.http.get(url, { withCredentials: true })
      .pipe(
        switchMap((tokens: any) => {
          const ulm: UserLoginModel = new UserLoginModel( this.getClaimsFromToken(tokens.idToken), tokens );
          this.setDistrictAppId(ulm.getAppId());
          localStorage.setItem('idToken', tokens.idToken);
          this.setAccessToken(tokens.accessToken);
          this.setSiteGuid(ulm.getSiteGuid());
          this._tokenRedeemed.next(ulm);
          return this.unService.updateLastSite(ulm, ulm.getSiteGuid())
            .pipe( catchError(() => of(undefined)));
        }),
        switchMap(() => this.unService.fetchPortalUserSetting(this.getUserLogin())), // redeemToken() makes the call to fetch, but redeemToken() isn't called on site switch
        catchError(err => {
          // this.temporary_deep_link = undefined;
          // const msg: string = err.error instanceof ErrorEvent ? err.error.message : err.message;
          // this.killUserObjAndShowErrorMsg(new ErrorDisplayData(UserMessage.genericFailureMsg, err, MessageCode.ERROR_AUTH_CODE_REDEEM_FAILED));
          throw err;
        })
      );
  }

  public getRemoveUser(): ErrorDisplayData {
    return this._removeUser.value;
  }
  public getRemoveUser$(): Observable<ErrorDisplayData> {
    return this.removeUser$;
  }
  private setRemoveUser(errorData: ErrorDisplayData) {
    this._removeUser.next(errorData);
  }

  public killUserObjAndShowErrorMsg(errorData: ErrorDisplayData) {
    this.setRemoveUser(errorData);
  }

  public logout(addl?: string): Observable<any> {
    this.stopAccessExpCheck();
    return this.http.get(this.envService.getPortalBackendUrl() + '/authenticate/logout' + (addl ? '?addl=' + addl : ''), { withCredentials: true });
  }

  public revokeAndLoginGuest(app?: Application, addl?: string) {
    this.newLoginInitiated();
    this.stopAccessExpCheck();
    let url = this.envService.getPortalBackendUrl() + '/authenticate/revokeAndLogin';

    if(this.envService.isBrowserPopupBlocked()) {
      url += this.getQueryParamsAndRelayState({
        districtAppId: this.getDistrictAppId(),
        siteGuid: this.getSiteGuid(),
        loginPrompt: LoginPrompt.NONE,
        fullscreen: true,
        app: app,
        skipGuestLoginPrompt: true,
        noIPAuth: this.getUserLogin()?.isSharedUser() ? true : false
      });
      localStorage.setItem('proxy-check-complete', 'false');
      ConsoleLogger.debug('xxxx AuthService.revokeAndLoginGuest() - lswitch url: ' + url);

      this.logFailInfo(undefined, {
        fn: 'revokeAndLoginGuest',
        addl: addl,
        loginPrompt: LoginPrompt.NONE,
        fullscreen: true,
        skipGuestLoginPrompt: true,
        noIPAuth: this.getUserLogin()?.isSharedUser() ? true : false
      }).pipe(take(1)).subscribe();

      this.envService.reloadEntireApp(url);
    } else {
      url += this.getQueryParamsAndRelayState({
        districtAppId: this.getDistrictAppId(),
        siteGuid: this.getSiteGuid(),
        loginPrompt: LoginPrompt.NONE,
        fullscreen: false
      });
      this.clientLoginService.open(url, PopupState.LOGIN);
    }
  }


  public getClaimsFromToken(idToken: any) {
    if (idToken) {
      return this.jwtHelper.decodeToken(idToken);
    }
    return undefined;
  }

  public redeemToken(token: string): Observable<any> {
    ConsoleLogger.debug('xxxx AuthService.redeemToken() - token: ' + token);

    if (!token) {
      ConsoleLogger.error('AuthService.redeemToken() called without token. Returning...');
      return of(undefined);
    }

    // Wait for app initialization to complete before redeeming token
    return this.envService.getAppInitComplete$()
      .pipe(
        tap(initComplete => {
          ConsoleLogger.debug('xxxx AuthService.redeemToken() - appInitComplete$ emitted: ' + initComplete);
        }),
        filter(initComplete => !!initComplete),
        take(1),
        switchMap(() => {
          ConsoleLogger.debug('xxxx AuthService.redeemToken() - app initialization complete, proceeding with token redemption');
          const url = this.envService.getPortalBackendUrl() + '/authenticate/redeem?code=' + token;
          return this.http.get(url, { withCredentials: true });
        }),
        switchMap((tokens: any) => {
          ConsoleLogger.debug('Redeem token successful! - ' + token);
          const ulm: UserLoginModel = new UserLoginModel( this.getClaimsFromToken(tokens.idToken), tokens );
          this.setRemoveUser(undefined);

          // No need to wait to see if debugging is enabled for this user. Just call it and continue.
          this.envService.checkDebuggingEnabled(ulm).pipe(take(1)).subscribe();

          const fetchUnsDistrictSettings$ = this.unService.fetchUnifiedDistrictSettings(ulm.getAppId())
            .pipe(
              catchError(() => {
                // Error should already be handled in the fetchUnifiedDistrictSettings call. This is just for safety.
                ConsoleLogger.error('Error fetching UNS district settings during token redeem');
                return of(undefined);
              })
            );
          const updateLastSite$ = this.unService.updateLastSite(ulm, ulm.getSiteGuid())
            .pipe(
              catchError(() => {
                // Error should already be handled in the updateLastSite call. This is just for safety.
                ConsoleLogger.error('Error update last site during token redeem');
                return of(undefined);
              })
            );

          return forkJoin([fetchUnsDistrictSettings$, updateLastSite$])
            .pipe(
              switchMap(() => {
                return this.unService.fetchPortalUserSetting(ulm)
                .pipe(
                  map(()=> {
                    ConsoleLogger.info('Destiny Build info: {version: ' + ulm.getDestinyVersion() + ', buildNumber: ' + this.envService.getRecommendedUNVersion() + '}');

                    // A district user (ulm.isDistrictUser) who is logging into a site (existingAuthEvent.siteGuid)
                    // without having site access will not receive a 'locationid' claim back from AASP (!ulm.siteGuid).
                    // In this case, don't update the ulm with the redeemed values. Instead, site_switch to the district
                    // which will trigger a new peek for the district user with an undefined siteGuid and take them to
                    // the district landing page.
                    // Else, proceed as normal.
                    const existingAuthEvent = this.getAuthenticationEvent();
                    if (existingAuthEvent && existingAuthEvent.siteGuid && ulm.isDistrictUser() && !ulm.getSiteGuid()) {
                      // don't update the ulm. Instead, switch user to the district...
                      this.setAuthenticationEvent(undefined);
                      this.setSiteGuid(undefined);
                      this.initiateAuthentication(new AuthenticationEvent(LoginPrompt.NONE, AuthType.DISTUSER_NO_SITE_DESTINY_HACK, undefined, 'no-site-access'));
                      return undefined;
                    } else {
                      this.setDistrictAppId(ulm.getAppId());
                      localStorage.setItem('idToken', tokens.idToken);
                      this.setAccessToken(tokens.accessToken);
                      this.setSiteGuid(ulm.getSiteGuid());
                      this._tokenRedeemed.next(ulm);
                      return ulm;
                    }
                  }),
                  catchError((err) => {
                    // This calls should already be handling errors. This is just for safety.
                    ConsoleLogger.error('Fetch portal user settings failed! - ' + token, err);
                    return of(ulm);
                  })
                );
              }),
              catchError((err) => {
                // This calls should already be handling errors. This is just for safety.
                ConsoleLogger.error('Update last site failed! - ' + token, err);
                return of(ulm);
              })
            );
        }),
        catchError(err => {
          ConsoleLogger.error('Redeem token failed! - ' + token + ' - ' + new Error('Failed to redeem').stack, err);
          this.temporary_deep_link = undefined;
          const msg: string = err.error instanceof ErrorEvent ? err.error.message : err.message;
          ConsoleLogger.error(`Access token = ${token} Error: ${msg}`);

          this.logFailInfo(undefined, {fn: 'redeemToken', token: token}, err).pipe(take(1)).subscribe();

          this.killUserObjAndShowErrorMsg(new ErrorDisplayData(UserMessage.genericFailureMsg, err, MessageCode.ERROR_AUTH_CODE_REDEEM_FAILED));
          throw err;
        })
      );
  }

  private doGrantsMatchCurrentUser(grants: GrantsModel): boolean {
    const ulm = this.getUserLogin();
    const notMatched =  !ulm
                        || grants.grantParams?.district_id !== ulm.getAppId()
                        || grants.grantParams?.site_guid !== ulm.getSiteGuid()
                        || grants.grantParams?.user_guid !== ulm.getSub();
    return !notMatched;
  }

  public fetchGrants(ulm: UserLoginModel): Observable<GrantsModel> {
    if (!ulm || !ulm.getSub()) {
      this.setGrants(undefined);
      return of(undefined);
    }

    const params = {
      access_token: this.getAccessToken(),
      district_id: ulm.getAppId(),
      user_guid: ulm.getSub()
    };

    if (ulm.getSiteGuid()) {
      _.set(params, 'site_guid', ulm.getSiteGuid());
    }

    const url = this.envService.getPortalBackendUrl() + '/user/grants';
    const grants$ = this.http.get<GrantsModel>(url, { params, withCredentials: true });
    const clm$ = this.isClmActive(ulm);

    return forkJoin([grants$, clm$])
      .pipe(
        map(([grants, isClmAvailable]) => {
          if (isClmAvailable && ulm.isDistrictUserNotAtSite()) {
            isClmAvailable = grants?.scopes?.indexOf(Scope.LIBRARY_ACCESS) >= 0;
          }
          if (this.doGrantsMatchCurrentUser(grants)) {
            grants.portalPermissions = { clmActive: isClmAvailable };
            // Since updating grants triggers PortalComponent to update the initial page for the new user,
            // wait until we know what apps can be selected before calling setGrants()
          } else {
            grants = undefined;
          }
          return grants;
        }),
        catchError((error) => {
          this.setGrants(undefined);
          throw error;
        })
      );
  }


  public refreshToken(frequencyInMillis: number, retryCount: number = 0, params?: any, cancelExisting: boolean = true): Observable<LoginTokenModel> {
    if (!cancelExisting && this.refreshInProgress) {
      ConsoleLogger.debug('^^^^ refreshToken() - Refresh already in progress, returning');
      return of(undefined);
    }
    this.refreshInProgress = true;

    // Cancel any existing refresh token calls
    ConsoleLogger.debug('^^^^ refreshToken() - Canceling any existing refresh calls');
    this.refreshUnsubscribe.next(undefined);
    this.refreshUnsubscribe.complete();
    this.refreshUnsubscribe = new Subject();

    const url = this.envService.getPortalBackendUrl() + '/authenticate/refresh';
    return this.http.get<LoginTokenModel>(url, {params, withCredentials: true })
      .pipe(
        tap((tokens: any) => {
          ConsoleLogger.debug('^^^^ AuthService - refreshToken - successful');
          this.setAccessToken(tokens.accessToken);
          delete tokens.accessToken;
          this.refreshInProgress = false;
        }),
        retry({
          count: retryCount,
          delay: (error: any, retryCount?: number) => {
            ConsoleLogger.debug(`^^^^ Retrying token refresh (attempt ${retryCount}) due to error. Retrying in ${frequencyInMillis}:`, error);
            return timer(frequencyInMillis);
          }
        }),
        catchError(err => {
          ConsoleLogger.debug('^^^^ AuthService - refreshToken - failed');
          this.stopAccessExpCheck();
          const msg: string = err.error instanceof ErrorEvent ? err.error.message : err.message;
          ConsoleLogger.error('Access token could not be refreshed. Error: ' + err.name + ' ' + msg);

          const duration = this.getAccessTokenDuration();
          const destinyTTL = this.getGrants()?.role?.sessionTimeoutMillis;
          const frequencyInMillis: number = this.getAaspKeepAliveFrequency(destinyTTL);
          this.logFailInfo(undefined, {fn: 'refreshToken', freq: frequencyInMillis, dttl: destinyTTL, dur: duration} ).pipe(take(1)).subscribe();

          this.killUserObjAndShowErrorMsg(new ErrorDisplayData(UserMessage.loginIssue));
          this.refreshInProgress = false;
          return of(undefined);
        }),
        takeUntil(this.refreshUnsubscribe)
      );
  }


  public startAccessExpCheck(destinyTTL: number = this.getGrants()?.role?.sessionTimeoutMillis) {
    ConsoleLogger.debug('^^^^ startAccessExpCheck() - Start Access Exp Check - destinyTTL: ' + destinyTTL);
    this.stopAccessExpCheck();
    const frequencyInMillis: number = this.getAaspKeepAliveFrequency(destinyTTL);
    timer(0, frequencyInMillis)
      .pipe(
        catchError(() => of(undefined)),
        takeUntil(this.accessTokenCheckInterval)
      )
      .subscribe(() => {
        ConsoleLogger.debug('^^^^ startAccessExpCheck() - Access Exp Check interval check...');
        if(this._userLogin.getValue()) {
          ConsoleLogger.debug('^^^^ Calling refresh Access token');
          this.refreshToken(frequencyInMillis, 1, undefined, false).subscribe();
        }
      });
}

  public getAccessTokenDuration() {
    const aaspTokenExpiration = this.getAccessToken() ? this.jwtHelper.decodeToken(this.getAccessToken())?.exp : undefined;
    const aaspTokenDuration = aaspTokenExpiration ? Math.abs(aaspTokenExpiration * 1000 - Date.now()) : this.AASP_ACCESS_TOKEN_DURATION;
    return aaspTokenDuration;
  }

  public getAaspKeepAliveFrequency(destinyTTLinMillis: number): number {
    let frequencyInMillis: number = this.envService.getAccessTokenExpCheckFrequency();
    const aaspTokenDurationinMillis = this.getAccessTokenDuration();
    if (destinyTTLinMillis) {
      if( destinyTTLinMillis > aaspTokenDurationinMillis ) {
       frequencyInMillis = aaspTokenDurationinMillis;
      } else {
        frequencyInMillis = destinyTTLinMillis;
      }
      frequencyInMillis = Math.floor(frequencyInMillis - (frequencyInMillis * 0.1));
    }
    frequencyInMillis = Math.floor(frequencyInMillis / 2); // We'll retry /refresh 1 time so cut the frequency in half
    ConsoleLogger.debug('^^^^ getAaspKeepAliveFrequency() - frequencyInMillis: ' + frequencyInMillis + ', destinyTTLinMillis: ' + destinyTTLinMillis + ', aaspTokenDurationinMillis: ' + aaspTokenDurationinMillis);
    return frequencyInMillis;
  }

  public stopAccessExpCheck() {
    ConsoleLogger.debug('^^^^ stopAccessExpCheck() - Stop Access Exp Check');

    this.accessTokenCheckInterval.next(undefined);
    this.accessTokenCheckInterval.complete();
    this.accessTokenCheckInterval = new Subject<void>();

    this.refreshUnsubscribe.next(undefined);
    this.refreshUnsubscribe.complete();
    this.refreshUnsubscribe = new Subject();
    this.refreshInProgress = false;
  }


  public updateTheme(ulm: UserLoginModel): Observable<any> {
    if (!ulm || !this.getSiteGuid()) {
      return of(undefined);
    }

    const params = {
      access_token: this.getAccessToken(),
      district_id: ulm.getAppId(),
      siteId: this.getSiteGuid(),
      userGuid: ulm.getSub()
    };

    return this.http.get<ThemeModel>(this.envService.getPortalBackendUrl() + '/user/themes',  { params, withCredentials: true })
      .pipe(
        tap(theme => this.themeService.loadThemeIntoDocument(theme?.themeName)),
        tap(rez => ConsoleLogger.debug(`Themes - ${JSON.stringify(rez)}`)),
        catchError(e => {
          ConsoleLogger.error('Unable to retrieve Themes' + JSON.stringify(e));
          return of(undefined);
        })
      );
  }

  public getAuthenticationUrl(loginAction: LoginAction): string {
    ConsoleLogger.debug('xxxx AuthService.getAuthenticationUrl() - loginAction: ' + JSON.stringify(new Error('blah')));
    let url = this.envService.getPortalBackendUrl() + '/authenticate';
    url += this.getQueryParamsAndRelayState(loginAction);
    return url;
  }

  private getQueryParamsAndRelayState(loginAction: LoginAction) {
    const relayState: any = {fullscreen: !!loginAction.fullscreen};

    if (loginAction.loginPrompt) {
      relayState.loginPrompt = loginAction.loginPrompt;
    }

    if (loginAction.app) {
      relayState.app = loginAction.app;
    }

    if(loginAction.continuationState) {
      relayState.continuationState = decodeURIComponent(loginAction.continuationState);
    }

    if (this.temporary_deep_link) {
      relayState.link = decodeURIComponent(this.temporary_deep_link);
    }

    if (!_.isEmpty(loginAction.fss_reason)) {
      relayState.fss_reason = loginAction.fss_reason;
    }

    let queryParams = '';
    if(!_.isEmpty(loginAction.districtAppId)) {
      relayState.districtId = loginAction.districtAppId;
      queryParams = '?appId=' + loginAction.districtAppId ;
      if(!_.isEmpty(loginAction.siteGuid)) {
        relayState.siteGuid = loginAction.siteGuid;
        queryParams += '&siteGuid=' + encodeURIComponent(loginAction.siteGuid);
      }
    }

    if (loginAction.skipGuestLoginPrompt) {
      relayState.skipGuestLoginPrompt = loginAction.skipGuestLoginPrompt;
    }

    const requestId = this.getRequestId();
    relayState.requestId = requestId;

    if(loginAction.noIPAuth) {
      queryParams += '&noIPAuth=true';
      relayState.noIPAuth = true;
    }

    const relayStateStr = encodeURIComponent(JSON.stringify(relayState));

    queryParams += !_.isEmpty(queryParams) ? '&loginPrompt=' + loginAction.loginPrompt : '?loginPrompt=' + loginAction.loginPrompt;
    queryParams += '&relayState=' + relayStateStr;
    queryParams += '&requestId=' + requestId;

    const showForgotPass = loginAction.forgotPassword !== undefined ? loginAction.forgotPassword : true;
    queryParams += '&fp=' + showForgotPass;
    const showCreateAccount = loginAction.createAccount !== undefined ? loginAction.createAccount : true;
    queryParams += '&ca=' + showCreateAccount;

    if (loginAction.ignorePreferredSite) {
      queryParams += '&ignorePreferredSite=true';
    }


    return queryParams;
  }


  public getRequestId(){
    return uuid.v4();
  }

  public getDiscoverAdminLaunchUrl(): string  {
    const ulm: UserLoginModel = this.getUserLogin();
    if (ulm) {
      const url = new URL(this.envService.getPortalBackendUrl() + '/authenticate/discoverAdminLtiLaunch');
      url.searchParams.append('appId', ulm.getAppId());
      url.searchParams.append('siteGuid', this.getSiteGuid());
      url.searchParams.append('userGuid', Util.getBaseUserGuid(ulm.getSub()));
      const shelfId = this.isDistrictUserNotAtSite() ? ulm.getDistrictFollettShelfId() : ulm.getFollettShelfId() ;
      url.searchParams.append('follettShelfSiteId', shelfId);
      url.searchParams.append('firstName', ulm.getFirstName());
      url.searchParams.append('lastName', ulm.getLastName());
      url.searchParams.append('userName', ulm.getUserName());
      // REMOVE THIS!!!
      // when we get the claim FOLLETT_SHELF_ADMIN for a district user both at district and site.
      // for now assigning true for a district user
      // REMOVE THIS!!!
      const shelfAdmin = this.isDistrictUser() || this.isDestinyAdmin() ? true : ulm.isFollettShelfAdmin() ;
      url.searchParams.append('isFollettShelfAdmin', String(shelfAdmin));
      url.searchParams.append('isClassSetAdmin', String(ulm.isClassSetEnabled()));
      url.searchParams.append('patronType', ulm.getPatronType());
      return url.toString();
    }
    return undefined;
  }

  public getAppAuthCodes(appIds: string, addlParam?: string): Observable<any> {
    const url = this.envService.getPortalBackendUrl() + '/authenticate/appCodes';
    const params: any = {
      appIds: appIds
    };
    if (addlParam) {
      params['addl'] = addlParam;
    }
    return this.http.get<any>(url, { params, withCredentials: true });
  }

  public isClmActive(ulm: UserLoginModel): Observable<boolean> {
    if (ulm.isDistrictUserNotAtSite() || ulm.hasRole([ImsRole.Faculty, ImsRole.Staff, ImsRole.Instructor, ImsRole.Administrator])) {
      const customerNumber: string = ulm.isDistrictUserNotAtSite() ? ulm.getDistrictCustomerNumber() : ulm.getFssSiteCustomerNumber();
      const clmSubUrl = this.envService.getPortalBackendUrl() + '/subscription/clm?customerNumber=' + customerNumber;
      return this.http.get(clmSubUrl, { withCredentials: true })
        .pipe(
          map((isActive) => isActive === true),
          catchError(error => {
            ConsoleLogger.error('Could not retrieve subscription information', error);
            return of(false);
          })
        );
    }
    return of(false);
  }

  private getTitlewaveLaunchUrl(): Observable<string> {
    const ulm: UserLoginModel = this.getUserLogin();
    if (ulm) {
      const params = {
        access_token: this.getAccessToken(),
        appId: ulm.getAppId(),
        siteGuid: this.getSiteGuid(),
        userGuid: ulm.getUserGuid()
      };

      const url = this.envService.getPortalBackendUrl() + '/authenticate/twLaunchUrl';
      return this.http.get<any>(url, { params, withCredentials: true })
        .pipe(
          map((rez) => _.get(rez, 'url')),
          tap((rez) => ConsoleLogger.debug('Titlewave url: ' + rez))
        );
    }

    return of(undefined);
  }

  private getFundraisingManagerLaunchUrl(): Observable<string> {
    const ulm: UserLoginModel = this.getUserLogin();
    if (ulm) {
      const params: any = {
        access_token: this.getAccessToken(),
        appId: ulm.getAppId()
      };

      if (ulm.getSiteGuid()) {
        params['siteGuid'] = ulm.getSiteGuid();
      }

      const url = this.envService.getPortalBackendUrl() + '/authenticate/launchToFundraisingManager';
      return this.http.get<any>(url, { params, withCredentials: true })
        .pipe(
          map((rez) => _.get(rez, 'url')),
          tap((rez) => ConsoleLogger.debug('FM url: ' + rez))
        );
    }

    return of(undefined);
  }

  private getMasterLibraryLaunchUrl(feature: Application): Observable<string> {
    const ulm: UserLoginModel = this.getUserLogin();
    if (ulm) {
      let featureParam: string = undefined;
      switch (feature) {
        case Application.MASTERLIBRARY_DRAWINGS:
          featureParam = 'ml_drawings';
          break;
        case Application.MASTERLIBRARY_SCHEDULES:
          featureParam = 'ml_schedules';
          break;
        case Application.MASTERLIBRARY_WORKORDERS:
          featureParam = 'ml_workorders';
          break;
        case Application.MASTERLIBRARY_HELPDESK:
          featureParam = 'mlw_helpdesk';
          break;
        case Application.MASTERLIBRARY_ITAM:
          featureParam = 'ml_itam';
          break;
      }

      const params: any = {
        access_token: this.getAccessToken(),
        appId: ulm.getAppId(),
        mlf: featureParam,
        cust: ulm.getDistrictCustomerNumber()
      };

      if (ulm.getSiteGuid()) {
        params['siteGuid'] = ulm.getSiteGuid();
      }
      const url = this.envService.getPortalBackendUrl() + '/ml/launch';
      return this.http.get<any>(url, { params, withCredentials: true })
        .pipe(
          map((rez) => _.get(rez, 'url')),
          map((url) => MLMarketingURLUtils.convertIfMLMarketingURL(url)),
          tap((rez) => {
            ConsoleLogger.debug('ML url: ' + rez);
          })
        );
    }
    return of(undefined);
  }

  private launchToVendorInternal(app: Application, url$: Observable<string>) {
    const win = window.open('', '_blank');
    // To avoid the popup blocker, open the new tab/window first,
    // then update that window.location with the application's url once it gets returned
    url$
      .pipe(
        take(1),
        catchError(err => {
          ConsoleLogger.error('Could not get ' + app + ' url -- ' + JSON.stringify(err));
          this.toastService.show(DialogModeType.ERROR,  UserMessage.launchToVendorFailed(app), true, undefined, undefined, JSON.stringify(err));
          win.close();
          return of(undefined);
        })
      )
      .subscribe((url: string) => {
        if (url && win) {
          win.location = url;
          try {
            win.opener = null;
          } catch (e) {
            console.log('Could not set win.opener to null: ' + e);
          }
        }
      });
  }

  public launchToMLVendor(app: Application.MASTERLIBRARY_DRAWINGS | Application.MASTERLIBRARY_SCHEDULES | Application.MASTERLIBRARY_WORKORDERS | Application.MASTERLIBRARY_HELPDESK | Application.MASTERLIBRARY_ITAM) {
    this.launchToVendorInternal(app, this.getMasterLibraryLaunchUrl(app));
  }
  public launchToVendor(app: Application.TITLEWAVE | Application.FM) {
    this.launchToVendorInternal(app, app === Application.FM ? this.getFundraisingManagerLaunchUrl() : this.getTitlewaveLaunchUrl());
  }

  public setSiteGuidParam(param: any) {
    this.siteGuidParam = param;
    if (param) {
      localStorage.setItem('siteGuidParam', param);
    } else {
      localStorage.removeItem('siteGuidParam');
    }
  }

  public getSiteGuidParam() {
    return this.siteGuidParam || localStorage.getItem('siteGuidParam');
  }

  getMLAppId$() {
    const ulm: UserLoginModel = this.getUserLogin();
    if (ulm) {
      const url = this.envService.getPortalBackendUrl() + '/ml/id';
      const params: any = {
        access_token: this.getAccessToken(),
        cust: ulm.getDistrictCustomerNumber()
      };

      return this.http.get<any>(url, { params, withCredentials: true })
        .pipe(
          map((rez) => _.get(rez, 'id')),
          tap((rez) => ConsoleLogger.debug('ML id: ' + rez))
        );
    } else {
      return of(undefined);
    }
  }

  public logUserInfoToConsole(msg: string, err?: any): void {
    let consoleMsg = '';
    const user = this.getUserLogin();
    if(user){
      consoleMsg =  msg
        + ' u=' + user.getUserGuid()
        + ' a=' + user.getAppId()
        + ' s=' + user.getSiteGuid()
        + ' t=' + user.getUserSessionTimeoutSeconds()
        + ' r=' + user.getRoles();
    }
    ConsoleLogger.error( consoleMsg, err );
  }

  public logFailInfo(ulm: UserLoginModel = this.getUserLogin(), params: { fn: string; [key: string]: any }, body: any = ''): Observable<any> {
    const enhancedParams: any = this.getDebugParams(params);
    enhancedParams.stimeout = ulm?.getUserSessionTimeoutSeconds();

    if (this.envService.isMessageDebuggingEnabled()) {
      // Log the message to UNS if this user/bx has debugging enabled...
      const debugMessage: DebugMessage = new DebugMessage(undefined, 'Portal', 'Portal', 'logevent', params as any, enhancedParams);
      const userGuid = ulm?.getUserGuid() || '';
      this.envService.addDebugMessage(userGuid, debugMessage);
      this.envService.processDebugMessageQueue().pipe(take(1)).subscribe();
    }

    // Log params on backend...
    return this.http.request('GET', this.envService.getPortalBackendUrl() + '/internal/log', { params: enhancedParams, body, withCredentials: true })
      .pipe(
        catchError((error) => {
          ConsoleLogger.error('Could not log fail info', error);
          return of(undefined);
        })
      );
  }


  public getDebugParams(params: any = {}): any{
    const ulm = this.getUserLogin();
    const userType =  !ulm ? 'na' :
                      ulm.isGuestUser() ? 'Guest' :
                      ulm.isDestinyAdmin() ? 'DA' :
                      ulm.isDistrictUserAtSite() ? 'DU@Site' :
                      ulm.isDistrictUserNotAtSite() ? 'DU!@Site' :
                      ulm.isAuthenticatedUser() ? 'AuthUser' :
                      '??';

    params['appid'] = _.isEmpty(params['appid']) ? (this.getDistrictAppId() || 'na') : params['appid'];
    params['bxid'] = _.isEmpty(params['bxid']) ? (this.envService.bxid || 'na') : params['bxid'];
    params['uid'] = _.isEmpty(params['uid']) ? (ulm?.getUserGuid() || 'na') : params['uid'];
    params['sid'] = _.isEmpty(params['sid']) ? (this.getSiteGuid() || 'na') : params['sid'];
    params['utype'] = _.isEmpty(params['utype']) ? userType : params['utype'];
    params['sha'] = version.sha || 'na';
    params['bi'] =  (navigator.userAgent.match(/(Chrome|Firefox|Safari|Edge|MSIE|Trident)\/?\s*(\d+)/) || ['na'])[0];
    params['os'] = (() => {
      const ua = navigator.userAgent;
      if (/Windows/i.test(ua)) return 'Windows';
      if (/CrOS/i.test(ua)) return 'ChromeOS';
      if (/Macintosh|Mac OS/i.test(ua)) return 'Mac';
      if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
      if (/Android/i.test(ua)) return 'Android';
      if (/Linux/i.test(ua)) return 'Linux';
      return 'Other';
    })();
    return params;
  }

}
